import { css } from "@emotion/css";
import { Badge, EmptyStateNoResults, Pagination, SearchInput, Tab, Tabs } from "@octopusdeploy/design-system-components";
import { space } from "@octopusdeploy/design-system-tokens";
import { useMutation, useQuery } from "@octopusdeploy/octopus-react-client";
import type { ChannelResource, EphemeralEnvironmentProjectStatus, GetEphemeralEnvironmentsOnboardingDataBffResponse, GetEphemeralEnvironmentsForProjectBffResponse, ProjectEphemeralEnvironmentDetail, ProjectEphemeralEnvironmentStatusCounts, ProjectResource, Repository, } from "@octopusdeploy/octopus-server-client";
import { Permission } from "@octopusdeploy/octopus-server-client";
import type { QueryParamValuesSetter } from "@octopusdeploy/portal-routes";
import * as React from "react";
import { useCallback } from "react";
import { EarlyAccessChip } from "~/components/Chips";
import { NotFound } from "~/components/NotFound/NotFound";
import { PageContent } from "~/components/PageContent/PageContent";
import { isAllowed } from "~/components/PermissionCheck/PermissionCheck";
import { useDebouncedExternalState } from "~/hooks/useDebouncedExternalState";
import type { ProjectContextProps } from "../../context";
import EphemeralEnvironmentCreationFailedCallout from "./EphemeralEnvironmentCreationFailedCallout";
import { EphemeralEnvironmentStatusIcon } from "./EphemeralEnvironmentStateIcon";
import { EphemeralEnvironmentsNoEnvironments } from "./EphemeralEnvironmentsNoEnvironments";
import { EphemeralEnvironmentsOnboarding } from "./EphemeralEnvironmentsOnboarding";
import { EphemeralEnvironmentsTable } from "./EphemeralEnvironmentsTable";
import EphemeralEnvironmentsTabs from "./EphemeralEnvironmentsTabs";
import { useEphemeralEnvironmentsEnabled } from "./useEphemeralEnvironmentsEnabled";
const validTabValues = ["all", "active", "pending", "failed", "deprovisioned"];
type TabValues = "all" | "active" | "pending" | "failed" | "deprovisioned";
type EphemeralEnvironmentsPageProps = {
    project: ProjectResource;
    queryParams: EphemeralEnvironmentsQueryParams;
    setQueryParams: QueryParamValuesSetter<EphemeralEnvironmentsQueryParams>;
    loaderData: EphemeralEnvironmentsPageData;
};
export interface EphemeralEnvironmentsQueryParams {
    page: number | undefined;
    pageSize: number | undefined;
    environmentName: string;
    status: TabValues;
}
type EphemeralEnvironmentsPageData = {
    environments: GetEphemeralEnvironmentsForProjectBffResponse;
    configurationState: EphemeralEnvironmentsConfigurationState;
    canViewAuditEvents: boolean;
};
type EphemeralEnvironmentsConfigured = {
    configured: true;
    channel: ChannelResource;
};
type EphemeralEnvironmentsNotConfigured = {
    configured: false;
    onboardingData: GetEphemeralEnvironmentsOnboardingDataBffResponse;
};
type EphemeralEnvironmentsConfigurationState = EphemeralEnvironmentsConfigured | EphemeralEnvironmentsNotConfigured;
const defaultPageSize = 30;
const pageSizeOptions = [defaultPageSize, 50, 100];
function getPagingValues(queryParams: EphemeralEnvironmentsQueryParams): {
    currentPage: number;
    pageSize: number;
} {
    const currentPage = queryParams.page ?? 1;
    const pageSize = queryParams.pageSize ?? defaultPageSize;
    return { currentPage, pageSize };
}
async function getEphemeralEnvironmentChannel(repository: Repository, project: ProjectResource) {
    const channels = await repository.Channels.allFromProject(project);
    return channels.find((channel) => channel.Type === "EphemeralEnvironment");
}
async function getEphemeralEnvironmentConfigurationState(repository: Repository, project: ProjectResource): Promise<EphemeralEnvironmentsConfigurationState> {
    const channel = await getEphemeralEnvironmentChannel(repository, project);
    return channel ? { configured: true, channel } : { configured: false, onboardingData: await repository.EphemeralEnvironments.getOnboardingData(project) };
}
async function getEnvironmentsForProject(repository: Repository, project: ProjectResource, queryParams: EphemeralEnvironmentsQueryParams) {
    const { currentPage, pageSize } = getPagingValues(queryParams);
    return await repository.EphemeralEnvironments.getForProject(project, (currentPage - 1) * pageSize, pageSize, queryParams.environmentName, statusFromTabValue(queryParams.status));
}
export async function ephemeralEnvironmentsPageLoader(repository: Repository, queryParams: EphemeralEnvironmentsQueryParams, projectContext: Promise<ProjectContextProps>): Promise<EphemeralEnvironmentsPageData> {
    const { state: { model: project }, } = await projectContext;
    const configurationStatePromise = getEphemeralEnvironmentConfigurationState(repository, project);
    const ephemeralEnvironmentsPromise = getEnvironmentsForProject(repository, project, queryParams);
    const hasEventPermission = isAllowed({
        permission: Permission.EventView,
    });
    return {
        environments: await ephemeralEnvironmentsPromise,
        configurationState: await configurationStatePromise,
        canViewAuditEvents: hasEventPermission,
    };
}
export function EphemeralEnvironmentsPage({ project, queryParams, setQueryParams, loaderData }: EphemeralEnvironmentsPageProps) {
    const ephemeralEnvironmentsEnabled = useEphemeralEnvironmentsEnabled();
    const [debouncedSearch, setDebouncedSearch] = useDebouncedExternalState(queryParams.environmentName, (value) => setQueryParams({ ...queryParams, environmentName: value, page: 1 }), 200);
    const { currentPage, pageSize } = getPagingValues(queryParams);
    const onPageChange = useCallback((page: number) => {
        setQueryParams({ ...queryParams, page });
    }, [queryParams, setQueryParams]);
    const onPageSizeChange = useCallback((pageSize: number) => {
        setQueryParams({ ...queryParams, pageSize: pageSize, page: 1 });
    }, [queryParams, setQueryParams]);
    const onTabChange = useCallback((tab: TabValues) => {
        setQueryParams({ ...queryParams, page: 1, status: tab });
    }, [queryParams, setQueryParams]);
    const { result: environmentsForProject, refetch: reloadEnvironmentsForProject } = useQuery(async (repository) => {
        return await getEnvironmentsForProject(repository, project, queryParams);
    }, [project, queryParams], "Get ephemeral environments for project", { refetchIntervalInMs: 5000, initialResult: loaderData.environments });
    const { result: configurationState, refetch: reloadConfigurationState } = useQuery(async (repository) => {
        return await getEphemeralEnvironmentConfigurationState(repository, project);
    }, [project], "Reload configuration", { initialResult: loaderData.configurationState });
    const { execute: retryProvisioning } = useMutation({
        name: "Retry provisioning of ephemeral environment",
        action: async (repository: Repository, environment: ProjectEphemeralEnvironmentDetail) => await repository.EphemeralEnvironments.retryProvisioning(project.SpaceId, project.Id, environment.Id),
        afterComplete: async () => reloadEnvironmentsForProject(),
    });
    const { execute: retryDeprovisioning } = useMutation({
        name: "Retry deprovisioning of ephemeral environment",
        action: async (repository: Repository, environment: ProjectEphemeralEnvironmentDetail) => await repository.EphemeralEnvironments.retryDeprovisioning(project.SpaceId, project.Id, environment.Id),
        afterComplete: async () => reloadEnvironmentsForProject(),
    });
    const { execute: markProvisioningSuccessful } = useMutation({
        name: "Mark provisioning as successful for ephemeral environment",
        action: async (repository: Repository, environment: ProjectEphemeralEnvironmentDetail) => await repository.EphemeralEnvironments.markProvisioningSuccessful(project.SpaceId, project.Id, environment.Id),
        afterComplete: async () => reloadEnvironmentsForProject(),
    });
    const { execute: markDeprovisioningSuccessful } = useMutation({
        name: "Mark deprovisioning as successful for ephemeral environment",
        action: async (repository: Repository, environment: ProjectEphemeralEnvironmentDetail) => await repository.EphemeralEnvironments.markDeprovisioningSuccessful(project.SpaceId, project.Id, environment.Id),
        afterComplete: async () => reloadEnvironmentsForProject(),
    });
    if (!ephemeralEnvironmentsEnabled) {
        return <NotFound />;
    }
    // Project does not have an ephemeral environments channel configured yet
    if (!configurationState.configured) {
        return (<PageContent header={ephemeralEnvironmentsHeader()}>
                <EphemeralEnvironmentsOnboarding project={project} onboardingData={configurationState.onboardingData} reloadConfiguration={reloadConfigurationState}/>
            </PageContent>);
    }
    const tabs = <EphemeralEnvironmentsTabs project={project} currentTab="overview"/>;
    // Channel is configured, but there are no environments. We check the status counts here, and not just the
    // list of environments because we don't want to show this just because we have typed in a search that does
    // not have any results
    if (!hasAnyEnvironments(environmentsForProject.StatusCounts)) {
        return (<>
                <PageContent header={ephemeralEnvironmentsHeader()} legacyTabs={tabs}>
                    <EphemeralEnvironmentCreationFailedCallout projectId={project.Id} canViewAuditEvents={loaderData.canViewAuditEvents} lastFailureEvent={loaderData.environments.EphemeralEnvironmentCreationFailedEvents.LastFailureEvent} failureSearchStartDate={loaderData.environments.EphemeralEnvironmentCreationFailedEvents.FailureSearchStartDate}/>
                    <EphemeralEnvironmentsNoEnvironments project={project} channel={configurationState.channel}/>
                </PageContent>
            </>);
    }
    return (<PageContent header={ephemeralEnvironmentsHeader()} legacyTabs={tabs} filters={{ inputs: [<SearchInput autoFocus={true} value={debouncedSearch} onChange={setDebouncedSearch} accessibleName={"Search ephemeral environments by name"} placeholder={"Search Environments"}/>] }} pagination={{
            ui: (<Pagination label="Environments" totalResults={environmentsForProject.Environments.TotalResults} itemsPerPageOptions={pageSizeOptions} selectedItemsPerPage={pageSize} currentPage={currentPage} onPageChange={onPageChange} onPageSizeChange={onPageSizeChange}/>),
            placement: "topAndBottom",
        }}>
            <>
                <EphemeralEnvironmentCreationFailedCallout projectId={project.Id} canViewAuditEvents={loaderData.canViewAuditEvents} lastFailureEvent={loaderData.environments.EphemeralEnvironmentCreationFailedEvents.LastFailureEvent} failureSearchStartDate={loaderData.environments.EphemeralEnvironmentCreationFailedEvents.FailureSearchStartDate}/>
                <EphemeralEnvironmentFilters statusCounts={environmentsForProject.StatusCounts} activeState={queryParams.status} onTabChange={onTabChange}/>
                {environmentsForProject && environmentsForProject.Environments.TotalResults === 0 ? (<EmptyStateNoResults />) : (<EphemeralEnvironmentsTable project={project} environments={environmentsForProject.Environments.Items} retryProvisioning={retryProvisioning} retryDeprovisioning={retryDeprovisioning} markProvisioningSuccessful={markProvisioningSuccessful} markDeprovisioningSuccessful={markDeprovisioningSuccessful} reloadEnvironmentsForProject={reloadEnvironmentsForProject}/>)}
            </>
        </PageContent>);
}
interface EphemeralEnvironmentFiltersProps {
    statusCounts: ProjectEphemeralEnvironmentStatusCounts;
    activeState: TabValues;
    onTabChange: (tab: TabValues) => void;
}
function EphemeralEnvironmentFilters({ statusCounts, activeState, onTabChange }: EphemeralEnvironmentFiltersProps) {
    const total = statusCounts.Provisioning + statusCounts.Provisioned + statusCounts.ProvisioningFailed + statusCounts.Deprovisioning + statusCounts.DeprovisioningFailed + statusCounts.Deprovisioned;
    function tabChanged(value: string) {
        if (isValidTabValue(value)) {
            onTabChange(value);
        }
    }
    return (<Tabs value={activeState} onChange={(e, value) => tabChanged(value)} scrollButtons="auto" variant="scrollable">
            <Tab value="all" label={<EphemeralEnvironmentFiltersTabLabel name="All" count={total}/>}/>
            <Tab value="active" label={<EphemeralEnvironmentFiltersTabLabel name="Active" status="Provisioned" count={statusCounts.Provisioned}/>}/>
            <Tab value="pending" label={<EphemeralEnvironmentFiltersTabLabel name="Pending" status="Provisioning" count={statusCounts.Provisioning + statusCounts.Deprovisioning}/>}/>
            <Tab value="failed" label={<EphemeralEnvironmentFiltersTabLabel name="Failed" status="ProvisioningFailed" count={statusCounts.ProvisioningFailed + statusCounts.DeprovisioningFailed}/>}/>
            <Tab value="deprovisioned" label={<EphemeralEnvironmentFiltersTabLabel name="Deprovisioned" status="Deprovisioned" count={statusCounts.Deprovisioned}/>}/>
        </Tabs>);
}
interface EphemeralEnvironmentFiltersTabProps {
    name: string;
    count: number;
    status?: EphemeralEnvironmentProjectStatus;
}
function EphemeralEnvironmentFiltersTabLabel({ name, count, status }: EphemeralEnvironmentFiltersTabProps) {
    const styles = {
        container: css({
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "center",
            gap: space[8],
        }),
    };
    return (<div className={styles.container}>
            {status && <EphemeralEnvironmentStatusIcon status={status} iconSize={16}/>} {name} <Badge variant="neutral" size="small" label={`${count}`}/>
        </div>);
}
function isValidTabValue(value: string): value is TabValues {
    return typeof value === "string" && validTabValues.includes(value);
}
export function ephemeralEnvironmentsHeader() {
    return {
        title: `Ephemeral Environments`,
        chip: <EarlyAccessChip />,
    };
}
function hasAnyEnvironments(statusCounts: ProjectEphemeralEnvironmentStatusCounts) {
    return statusCounts.Provisioning > 0 || statusCounts.Provisioned > 0 || statusCounts.ProvisioningFailed > 0 || statusCounts.Deprovisioning > 0 || statusCounts.Deprovisioned > 0 || statusCounts.DeprovisioningFailed > 0;
}
function statusFromTabValue(tabValue: TabValues): EphemeralEnvironmentProjectStatus[] {
    switch (tabValue) {
        case "active":
            return ["Provisioned"];
        case "pending":
            return ["Provisioning", "Deprovisioning"];
        case "failed":
            return ["ProvisioningFailed", "DeprovisioningFailed"];
        case "deprovisioned":
            return ["Deprovisioned"];
        default:
            return [];
    }
}
